import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import i18n from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

const TOKEN_KEY = "session_token";
export const getStoredToken = () => localStorage.getItem(TOKEN_KEY);
export const setStoredToken = (t) => t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY);

axios.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = getStoredToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return null;
    }
    try {
      const r = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(r.data);
      // Il selettore lingua manuale e' disabilitato (vedi utils/language.js):
      // la lingua mostrata e' decisa esclusivamente dal country dell'account,
      // ad ogni checkAuth, senza eccezioni. Ripuliamo anche eventuali flag
      // manuali rimasti in localStorage da prima della disattivazione, cosi'
      // un dispositivo con uno stato "vecchio" si autocorregge al prossimo login.
      if (r.data?.country) {
        const lang = r.data.country === "FR" ? "fr" : "it";
        localStorage.setItem("userCountryLang", lang);
        localStorage.removeItem("userLanguageManual");
        if (i18n.language !== lang) i18n.changeLanguage(lang);
      }
      return r.data;
    } catch {
      setStoredToken(null);
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_token=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    setStoredToken(null);
    setUser(null);
    // La preferenza lingua e' legata all'account che l'ha impostata: il
    // prossimo utente che fa login su questo browser (o visita il sito senza
    // loggarsi) deve ripartire dal rilevamento automatico (?lang= in URL o
    // lingua del browser), non ereditare quella dell'account precedente.
    localStorage.removeItem("userLanguageManual");
    localStorage.removeItem("userCountryLang");
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
